import { useNavigate, useSearchParams } from "react-router-dom";

import { useAgents } from "@/api/agents";
import { useJobs } from "@/api/jobs";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatJobNumber } from "@/lib/format";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 50;

export function JobList() {
  const [params, setParams] = useSearchParams();
  const cursor = params.get("cursor") ?? undefined;
  const navigate = useNavigate();
  const query = useJobs({ limit: PAGE_SIZE, cursor });
  const { agentsByType } = useAgents();

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="label-mono">Jobs</p>
          <h1 className="font-display text-4xl text-text">Recent runs</h1>
        </div>
      </div>

      {query.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="No jobs yet"
          description="Run `pnpm smoke:curator` to dispatch a curator job, then refresh."
        />
      ) : (
        <>
          <div className="border border-border rounded bg-bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/jobs/${formatJobNumber(job.jobNumber)}`)
                    }
                  >
                    <TableCell className="font-mono text-xs text-accent">
                      <span title={job.id}>#{formatJobNumber(job.jobNumber)}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span title={job.agentType}>
                        {agentsByType[job.agentType]?.name ?? job.agentType}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={job.status} />
                    </TableCell>
                    <TableCell className="text-xs">
                      <RelativeTime date={job.createdAt} />
                    </TableCell>
                    <TableCell className="text-xs">
                      <RelativeTime date={job.updatedAt} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {query.data.nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setParams({ cursor: query.data.nextCursor as string });
                }}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
